import {
  type MaintenanceWorkOrderListQuery,
  type MaintenanceWorkOrderStatus,
} from '@gb-ticket/shared-types';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MaintenanceOptionalSelect } from '@/products/maintenance/features/maintenance-filter-select';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import { nextMaintenanceColumnSort } from '@/products/maintenance/features/maintenance-list-query';
import {
  maintenanceTrashPath,
  maintenanceTrashQueryFromSearch,
} from '@/products/maintenance/features/maintenance-trash-path';
import {
  maintenanceWorkOrderCustomerColumn,
  maintenanceWorkOrderDueColumn,
  maintenanceWorkOrderInstallationColumn,
  maintenanceWorkOrderScheduledColumn,
  maintenanceWorkOrderTechnicianColumn,
} from '@/products/maintenance/features/maintenance-work-order-list-columns';
import {
  isMaintenanceWorkOrderSortBy,
  MAINTENANCE_PLANNING_SORT_FIELDS,
  useRememberedWorkOrderSort,
  writeMaintenanceWorkOrderListSort,
} from '@/products/maintenance/features/maintenance-work-order-list-sort';
import { MaintenanceListToolbar } from '@/products/maintenance/features/maintenance-list-toolbar';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import { useMaintenanceRowSelection } from '@/products/maintenance/features/maintenance-row-selection';
import { MAINTENANCE_WORK_ORDER_STATUS_OPTIONS } from '@/products/maintenance/features/maintenance-status';
import { MaintenanceWorkOrderTrashBar } from '@/products/maintenance/features/work-orders/maintenance-work-order-trash-bar';
import { useTrashedMaintenanceWorkOrders } from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import { maintenanceWorkOrderPath } from '@/products/maintenance/maintenance-paths';

export function MaintenanceTrashPage() {
  usePageTitle('Papierkorb');
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const parsedQuery = useMemo(() => maintenanceTrashQueryFromSearch(search.toString()), [search]);
  const query = useRememberedWorkOrderSort(
    search.toString(),
    'trash',
    MAINTENANCE_PLANNING_SORT_FIELDS,
    parsedQuery,
  );
  const list = useTrashedMaintenanceWorkOrders(query);
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const visibleIds = useMemo(() => items.map((order) => order.id), [items]);
  const selection = useMaintenanceRowSelection(visibleIds);
  const filtered = Boolean(query.status);

  function apply(patch: Partial<MaintenanceWorkOrderListQuery>) {
    void navigate(maintenanceTrashPath({ ...query, ...patch, page: patch.page ?? 1 }));
  }

  function resetFilters() {
    void navigate(
      maintenanceTrashPath({
        page: 1,
        pageSize: query.pageSize,
        sortBy: query.sortBy,
        sortOrder: query.sortOrder,
      }),
    );
  }

  function onColumnSort(sortBy: string) {
    if (!isMaintenanceWorkOrderSortBy(sortBy, MAINTENANCE_PLANNING_SORT_FIELDS)) {
      return;
    }
    const next = nextMaintenanceColumnSort(query, sortBy);
    writeMaintenanceWorkOrderListSort('trash', { sortBy, sortOrder: next.sortOrder });
    apply(next);
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Papierkorb"
        description="Aufträge aus Planung und Archiv. Wiederherstellen legt sie zurück. Endgültig löschen entfernt Auftrag und Dateien."
      />
      <section className="space-y-3">
        <MaintenanceListToolbar filtered={filtered} onReset={resetFilters}>
          <MaintenanceOptionalSelect
            label="Status"
            id="trash-status"
            value={query.status ?? ''}
            onValueChange={(status) =>
              apply({
                status: status ? (status as MaintenanceWorkOrderStatus) : undefined,
              })
            }
            items={MAINTENANCE_WORK_ORDER_STATUS_OPTIONS}
          />
        </MaintenanceListToolbar>
        <MaintenanceWorkOrderTrashBar
          orders={items}
          selectedIds={selection.selectedIds}
          onClear={() => selection.clear()}
        />
        <MaintenanceRecordList
          rows={items}
          pending={list.isPending ? 'Papierkorb wird geladen …' : undefined}
          emptyLabel="Der Papierkorb ist leer."
          filtered={filtered}
          onReset={resetFilters}
          getRowId={(order) => order.id}
          getHref={(order) => maintenanceWorkOrderPath(order.id)}
          getTitle={(order) => order.orderNumber}
          getMeta={(order) =>
            `${order.customer.name} · ${order.installation.name} · ${order.assignedTo.displayName}`
          }
          getStatus={(order) => order.status}
          selection={selection}
          titleHeader="Auftrag"
          titleSortKey="orderNumber"
          statusSortKey="status"
          sort={{
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            onSort: onColumnSort,
          }}
          columns={[
            maintenanceWorkOrderCustomerColumn(),
            maintenanceWorkOrderInstallationColumn(),
            maintenanceWorkOrderTechnicianColumn(),
            maintenanceWorkOrderScheduledColumn(),
            maintenanceWorkOrderDueColumn(),
          ]}
        />
        {items.length > 0 && list.data ? (
          <MaintenanceListFooter
            page={list.data.page}
            pageSize={list.data.pageSize}
            total={list.data.total}
            onPageChange={(page) => apply({ page })}
          />
        ) : null}
      </section>
    </div>
  );
}
